import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Invites() {
  const [invites, setInvites] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ role: "atleta", athlete_id: "", label: "" });
  const [created, setCreated] = useState(null);

  const load = async () => {
    const [i, a] = await Promise.all([api.get("/invites"), api.get("/athletes")]);
    setInvites(i.data);
    setAthletes(a.data);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      const payload = {
        role: form.role,
        athlete_id: form.athlete_id || null,
        label: form.label || "",
      };
      const res = await api.post("/invites", payload);
      setCreated(res.data);
      setForm({ role: "atleta", athlete_id: "", label: "" });
      load();
    } catch (e) {
      toast.error("Falha ao gerar código");
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Código copiado!");
  };

  const revoke = async (code) => {
    if (!window.confirm(`Revogar código ${code}?`)) return;
    await api.delete(`/invites/${code}`);
    toast.success("Código revogado");
    load();
  };

  const closeDialog = () => {
    setOpen(false);
    setCreated(null);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-500">Acesso da equipe</div>
            <h1 className="font-display font-black text-5xl uppercase tracking-tight mt-1">Códigos de Convite</h1>
            <p className="text-zinc-400 text-sm mt-2 max-w-2xl">
              Gere códigos para que atletas (ou outros técnicos) acessem o sistema sem precisar de conta Google. Cada código é de uso único e expira em 30 dias.
            </p>
          </div>
          <Dialog open={open} onOpenChange={(v) => { if (!v) closeDialog(); else setOpen(true); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-invite-btn" className="bg-[#FF3B30] hover:bg-red-600 text-white font-bold uppercase tracking-wide rounded-sm">
                + Novo Código
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#141414] border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl uppercase">
                  {created ? "Código Gerado" : "Novo Código de Convite"}
                </DialogTitle>
              </DialogHeader>

              {!created ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Função</label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, athlete_id: "" })}>
                      <SelectTrigger className="mt-2 bg-[#0A0A0A] border-zinc-800 text-white" data-testid="invite-role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141414] border-zinc-800 text-white">
                        <SelectItem value="atleta">Atleta</SelectItem>
                        <SelectItem value="tecnico">Técnico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {form.role === "atleta" && athletes.length > 0 && (
                    <div>
                      <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
                        Vincular a Atleta Existente <span className="text-zinc-600 normal-case tracking-normal">(opcional)</span>
                      </label>
                      <Select value={form.athlete_id || "none"} onValueChange={(v) => setForm({ ...form, athlete_id: v === "none" ? "" : v })}>
                        <SelectTrigger className="mt-2 bg-[#0A0A0A] border-zinc-800 text-white" data-testid="invite-athlete">
                          <SelectValue placeholder="Criar novo perfil" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#141414] border-zinc-800 text-white max-h-72">
                          <SelectItem value="none">— Criar novo perfil ao acessar —</SelectItem>
                          {athletes.map((a) => (
                            <SelectItem key={a.athlete_id} value={a.athlete_id}>
                              {a.name} {a.number ? `#${a.number}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Rótulo (opcional)</label>
                    <Input
                      data-testid="invite-label-input"
                      value={form.label}
                      onChange={(e) => setForm({ ...form, label: e.target.value })}
                      placeholder="Ex: Maria - temporada 2026"
                      className="mt-2 bg-[#0A0A0A] border-zinc-800 text-white"
                    />
                  </div>

                  <DialogFooter>
                    <Button onClick={handleCreate} data-testid="confirm-create-invite" className="bg-[#FF3B30] hover:bg-red-600 text-white font-bold uppercase rounded-sm">
                      Gerar Código
                    </Button>
                  </DialogFooter>
                </div>
              ) : (
                <div className="space-y-4" data-testid="invite-created">
                  <div className="bg-[#0A0A0A] border border-[#FF3B30] rounded-sm p-6 text-center">
                    <div className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-500 mb-2">Código de Acesso</div>
                    <div className="font-display font-black text-5xl tracking-[0.3em] text-[#FF3B30]" data-testid="generated-code">
                      {created.code}
                    </div>
                    <div className="text-xs text-zinc-500 mt-2 uppercase tracking-widest">
                      {created.role === "tecnico" ? "Acesso Técnico" : "Acesso Atleta"}
                      {created.label ? ` • ${created.label}` : ""}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      onClick={() => copy(created.code)}
                      data-testid="copy-code-btn"
                      className="bg-zinc-800 hover:bg-zinc-700 text-white font-bold uppercase rounded-sm"
                    >
                      Copiar
                    </Button>
                    <Button
                      onClick={closeDialog}
                      className="bg-[#FF3B30] hover:bg-red-600 text-white font-bold uppercase rounded-sm"
                    >
                      Concluir
                    </Button>
                  </div>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-widest text-center">
                    Compartilhe o código com a atleta. Expira em 30 dias.
                  </p>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-[#141414] border border-zinc-800 rounded-md p-6">
          {invites.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">Nenhum código gerado ainda.</p>
          ) : (
            <table className="w-full text-sm" data-testid="invites-table">
              <thead>
                <tr className="text-zinc-500 uppercase text-xs tracking-wider text-left">
                  <th className="py-2">Código</th>
                  <th>Função</th>
                  <th>Rótulo</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => {
                  const used = !!inv.used_by;
                  const expired = inv.expires_at && new Date(inv.expires_at) < new Date();
                  return (
                    <tr key={inv.code} className="border-t border-zinc-800">
                      <td className="py-3">
                        <span className="font-display font-black text-lg tracking-[0.2em]">{inv.code}</span>
                      </td>
                      <td className="text-zinc-300 uppercase text-xs tracking-wider font-bold">
                        {inv.role === "tecnico" ? "Técnico" : "Atleta"}
                      </td>
                      <td className="text-zinc-400">{inv.label || "—"}</td>
                      <td>
                        {used ? (
                          <span className="text-xs uppercase font-bold tracking-wider bg-zinc-800 text-zinc-400 px-2 py-1 rounded-sm">Usado</span>
                        ) : expired ? (
                          <span className="text-xs uppercase font-bold tracking-wider bg-red-900/30 text-red-400 px-2 py-1 rounded-sm">Expirado</span>
                        ) : (
                          <span className="text-xs uppercase font-bold tracking-wider bg-[#22C55E]/20 text-[#22C55E] px-2 py-1 rounded-sm">Ativo</span>
                        )}
                      </td>
                      <td className="text-right">
                        {!used && !expired && (
                          <button
                            onClick={() => copy(inv.code)}
                            data-testid={`copy-${inv.code}`}
                            className="text-xs text-zinc-400 hover:text-white px-2 py-1 mr-2"
                          >
                            Copiar
                          </button>
                        )}
                        <button
                          onClick={() => revoke(inv.code)}
                          data-testid={`revoke-${inv.code}`}
                          className="text-xs text-red-400 hover:text-red-300 px-2 py-1"
                        >
                          Revogar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
